import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Format currency value
 */
export function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) {
    return 'Price on request';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Get view type display label
 */
export function getViewTypeLabel(viewType: 'lagoon' | 'canal'): string {
  return viewType === 'lagoon' ? 'Lagoon View' : 'Canal View';
}

/**
 * Format floor enum value or description for display
 * Converts database enum values (GF, second, third, fourth) or configuration descriptions
 * to display format (GF, 2F, 3F, 4F)
 */
export function formatFloorDisplay(floor: 'GF' | 'second' | 'third' | 'fourth' | string): string {
  // Handle enum values first
  switch (floor) {
    case 'GF':
      return 'GF';
    case 'second':
      return '2F';
    case 'third':
      return '3F';
    case 'fourth':
      return '4F';
  }
  
  // Handle configuration floor descriptions
  const normalized = floor.trim();
  
  if (normalized === 'Ground Floor' || normalized.includes('Ground Floor')) {
    return 'GF';
  }
  if (normalized === 'Second Floor' || normalized === '2nd Floor' || normalized.includes('Second Floor')) {
    return '2F';
  }
  if (normalized === 'Third Floor' || normalized === '3rd Floor' || normalized.includes('3rd Floor') || normalized.includes('Third Floor')) {
    return '3F';
  }
  if (normalized === '4-Storey Townhouse' || normalized.includes('4-Storey Townhouse')) {
    return 'GF'; // Townhouses start at ground floor
  }
  if (normalized.includes('2-Storey Penthouse') || normalized.includes('2-Storey Apartment')) {
    return '3F'; // Penthouses/apartments on 3rd floor
  }
  
  // Fallback: return as-is for unknown values
  return floor;
}

/**
 * Get badge color classes for unit availability status
 * Returns Tailwind CSS classes using theme status colors
 */
export function getStatusBadgeClasses(status: 'available' | 'reserved' | 'sold' | string): string {
  switch (status.toLowerCase()) {
    case 'available':
      return 'bg-status-available hover:bg-status-available/90 text-white border-status-available whitespace-nowrap';
    case 'reserved':
      return 'bg-status-reserved hover:bg-status-reserved/90 text-white border-status-reserved whitespace-nowrap';
    case 'sold':
      return 'bg-status-sold hover:bg-status-sold/90 text-white border-status-sold whitespace-nowrap';
    default:
      return 'bg-muted hover:bg-muted/80 text-muted-foreground border-muted whitespace-nowrap';
  }
}

/**
 * Convert HSL color values to hex format
 * @param h Hue (0-360)
 * @param s Saturation (0-100)
 * @param l Lightness (0-100)
 * @returns Hex color string (e.g., "#567C8D")
 */
function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`.toUpperCase();
}

/**
 * Get CSS variable value as hex color for SVG use
 * Reads CSS variable from DOM and converts HSL to hex format
 * Handles nested CSS variable references (e.g., var(--status-available) -> var(--brand-teal) -> HSL value)
 * @param cssVar CSS variable name (e.g., "--status-available")
 * @param fallback Fallback hex color if variable not found
 * @returns Hex color string (e.g., "#567C8D")
 */
export function getCssVarAsHex(cssVar: string, fallback: string = '#000000'): string {
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const root = document.documentElement;
    let value = getComputedStyle(root).getPropertyValue(cssVar).trim();
    
    if (!value) {
      return fallback;
    }

    // Resolve nested CSS variable references (e.g., "var(--brand-teal)" -> actual HSL value)
    // Limit recursion to prevent infinite loops
    let depth = 0;
    const maxDepth = 5;
    while (value.startsWith('var(') && depth < maxDepth) {
      const varMatch = value.match(/var\(([^)]+)\)/);
      if (varMatch) {
        const nestedVar = varMatch[1].trim();
        value = getComputedStyle(root).getPropertyValue(nestedVar).trim();
        depth++;
      } else {
        break;
      }
    }

    if (!value) {
      return fallback;
    }

    // Handle HSL format: "197 24% 44%" (space-separated, no hsl() wrapper)
    const hslMatch = value.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
    if (hslMatch) {
      const h = parseFloat(hslMatch[1]);
      const s = parseFloat(hslMatch[2]);
      const l = parseFloat(hslMatch[3]);
      return hslToHex(h, s, l);
    }

    // Handle hsl() format: "hsl(197, 24%, 44%)"
    const hslFunctionMatch = value.match(/hsl\((\d+(?:\.\d+)?),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/);
    if (hslFunctionMatch) {
      const h = parseFloat(hslFunctionMatch[1]);
      const s = parseFloat(hslFunctionMatch[2]);
      const l = parseFloat(hslFunctionMatch[3]);
      return hslToHex(h, s, l);
    }

    // If already hex, return as-is
    if (value.startsWith('#')) {
      return value;
    }

    return fallback;
  } catch (error) {
    console.warn(`Failed to get CSS variable ${cssVar}:`, error);
    return fallback;
  }
}

/**
 * Get status color as hex for SVG fill attributes
 * Reads the underlying CSS variables directly for reliability
 * @param status Unit availability status
 * @returns Hex color string
 */
export function getStatusColorHex(status: 'available' | 'reserved' | 'sold' | string): string {
  let color: string;
  switch (status.toLowerCase()) {
    case 'available':
      // --status-available maps to --brand-teal: 197 24% 44% = #567C8D
      color = getCssVarAsHex('--brand-teal', '#567C8D');
      // Debug: Log if fallback is used (indicates CSS variable not found)
      if (color === '#567C8D' && typeof window !== 'undefined') {
        const cssValue = getComputedStyle(document.documentElement).getPropertyValue('--brand-teal').trim();
        if (!cssValue) {
          console.warn('CSS variable --brand-teal not found, using fallback #567C8D');
        }
      }
      return color;
    case 'reserved':
      // --status-reserved maps to --champagne-light: 197 30% 55%
      return getCssVarAsHex('--champagne-light', '#7A9BA8');
    case 'sold':
      // --status-sold maps to --brand-navy: 213 29% 26% = #2f4156
      return getCssVarAsHex('--brand-navy', '#2f4156');
    default:
      return '#e5e7eb'; // default gray
  }
}
